import { cloneDeep } from 'lodash-es';
import uuidv4 from 'uuid/v4';
import { createProxiedDoc } from '@marvelapp/crdt';
import * as ConditionalLogicUtils from '../utils/conditionalLogicUtils';
import { generateUUID } from '../utils/generateUUID';
import { STEP_PREFIXES } from './constants';
import { create as createInstructionStep } from './instructionStep';
import { create as createMediaSettingsStep } from './mediaSettingsStep';
import { getAllSteps, getAnyMediaEnabled, getRecordingPermissions, } from './selectors';
import * as StepUtils from './stepUtils';
import { StepClass, } from './types';
export function createUserTestDoc(data, options) {
    const userTest = createProxiedDoc(data, 'usertest', options);
    return userTest;
}
export function generateUserTestUUID() {
    return generateUUID('ut_');
}
export function stripUuidPrefix(uuid) {
    return uuid.replace(/^ut_/, '');
}
export const defaultState = {
    uuid: generateUserTestUUID(),
    schemaVersion: 0,
    revision: 0,
    steps: [],
    stepDefinitions: {},
    welcomeStep: createInstructionStep(),
    createdAt: Date.now(),
    exitStep: createInstructionStep({ hasButton: false }),
};
export function create(partial = {}) {
    return Object.assign({ uuid: generateUserTestUUID(), schemaVersion: 0, revision: 0, steps: [], stepDefinitions: {}, studies: {}, studyIDs: [], welcomeStep: createInstructionStep(), createdAt: Date.now(), exitStep: createInstructionStep({ hasButton: false }) }, partial);
}
export function insertStep(mutable, step, activeStepUUID) {
    mutable.stepDefinitions[step.uuid] = step;
    const insertIndex = getInsertIndex(mutable, activeStepUUID);
    mutable.steps.splice(insertIndex, 0, step.uuid);
}
export function deleteStep(mutable, stepUUID) {
    const index = getStepIndex(mutable, stepUUID);
    mutable.steps.splice(index, 1);
    delete mutable.stepDefinitions[stepUUID];
    ConditionalLogicUtils.deleteAssociatedStepRules(mutable, stepUUID);
}
function getInsertIndex(userTest, activeStepUUID) {
    if (activeStepUUID === null) {
        return userTest.steps.length;
    }
    if (activeStepUUID === userTest.welcomeStep.uuid) {
        return 0;
    }
    if (activeStepUUID === userTest.exitStep.uuid) {
        return userTest.steps.length;
    }
    return getStepIndex(userTest, activeStepUUID) + 1;
}
function getStepIndex(userTest, stepUUID) {
    return userTest.steps.findIndex((uuid) => uuid === stepUUID);
}
export function cloneAndAddStep(mutable, step, insertionIndex) {
    const newStep = cloneStep(step, false);
    mutable.steps.splice(insertionIndex, 0, newStep.uuid);
    mutable.stepDefinitions[newStep.uuid] = newStep;
}
export function reorderStep(userTest, startIndex, endIndex) {
    const [removed] = userTest.steps.splice(startIndex, 1);
    userTest.steps.splice(endIndex, 0, removed);
    return removed;
}
export function clone(userTest, newTestUUID = generateUserTestUUID()) {
    const { createdAt } = userTest;
    const clonedTest = create({
        uuid: newTestUUID,
        schemaVersion: userTest.schemaVersion,
        createdAt,
    });
    // create a mapping of original step uuids to the cloned step uuids
    const stepUUIDMap = {};
    clonedTest.welcomeStep = cloneStep(userTest.welcomeStep);
    stepUUIDMap[userTest.welcomeStep.uuid] = clonedTest.welcomeStep.uuid;
    clonedTest.exitStep = cloneStep(userTest.exitStep);
    stepUUIDMap[userTest.exitStep.uuid] = clonedTest.exitStep.uuid;
    if (userTest.mediaSettingsStep) {
        clonedTest.mediaSettingsStep = cloneStep(userTest.mediaSettingsStep);
    }
    if (userTest.recordingPermissions) {
        clonedTest.recordingPermissions = userTest.recordingPermissions;
    }
    userTest.steps.forEach((uuid) => {
        const step = userTest.stepDefinitions[uuid];
        const clonedStep = cloneStep(step);
        stepUUIDMap[step.uuid] = clonedStep.uuid;
        clonedTest.stepDefinitions[clonedStep.uuid] = clonedStep;
        clonedTest.steps.push(clonedStep.uuid);
    });
    clonedTest.steps.forEach((uuid) => {
        var _a, _b, _c, _d, _e;
        const step = clonedTest.stepDefinitions[uuid];
        if ('conditions' in step) {
            if (StepUtils.isOfType(step, StepClass.YesOrNo)) {
                if ((_a = step.conditions) === null || _a === void 0 ? void 0 : _a.answer.true) {
                    step.conditions.answer.true =
                        stepUUIDMap[step.conditions.answer.true];
                }
                if ((_b = step.conditions) === null || _b === void 0 ? void 0 : _b.answer.false) {
                    step.conditions.answer.false =
                        stepUUIDMap[step.conditions.answer.false];
                }
            }
            if (StepUtils.isChoiceStep(step)) {
                if ((_c = step.conditions) === null || _c === void 0 ? void 0 : _c.rules.length) {
                    step.conditions.rules.forEach(({ destinationStepUUID }, index) => {
                        step.conditions.rules[index].destinationStepUUID =
                            stepUUIDMap[destinationStepUUID];
                    });
                }
            }
            if ('conditions' in step && ((_d = step.conditions) === null || _d === void 0 ? void 0 : _d.ALWAYS)) {
                step.conditions.ALWAYS = stepUUIDMap[(_e = step.conditions) === null || _e === void 0 ? void 0 : _e.ALWAYS];
            }
        }
    });
    return clonedTest;
}
function cloneStep(step, shouldCloneLogic = true) {
    const newUUID = generateUUID(STEP_PREFIXES[step.className]);
    if (StepUtils.isOfType(step, StepClass.MediaSettings)) {
        const clonedStep = cloneDeep(step);
        clonedStep.uuid = newUUID;
        clonedStep.revision = 0;
        return clonedStep;
    }
    if (StepUtils.isOneOfType(step, [
        StepClass.Question,
        StepClass.Instruction,
        StepClass.PrototypeTask,
        StepClass.WebsiteTask,
        StepClass.RatingScale,
        StepClass.FiveSecondTest,
    ])) {
        const clonedStep = cloneDeep(step);
        clonedStep.uuid = newUUID;
        clonedStep.revision = 0;
        if (!shouldCloneLogic && (clonedStep === null || clonedStep === void 0 ? void 0 : clonedStep.conditions)) {
            clonedStep.conditions.ALWAYS = null;
        }
        return clonedStep;
    }
    if (StepUtils.isOfType(step, StepClass.YesOrNo)) {
        const clonedStep = cloneDeep(step);
        clonedStep.uuid = newUUID;
        clonedStep.revision = 0;
        if (!shouldCloneLogic && clonedStep.conditions) {
            clonedStep.conditions.answer.true = null;
            clonedStep.conditions.answer.false = null;
            clonedStep.conditions.ALWAYS = null;
        }
        return clonedStep;
    }
    if (StepUtils.isChoiceStep(step)) {
        const clonedStep = cloneChoiceStep(step, newUUID, shouldCloneLogic);
        return clonedStep;
    }
    return assertUnreachable(step);
}
function cloneChoiceStep(step, uuid, shouldCloneLogic) {
    const clonedStep = cloneDeep(step);
    clonedStep.uuid = uuid;
    clonedStep.revision = 0;
    const choicesMap = {};
    step.choices.forEach((choiceUUID, i) => {
        const choice = step.choiceDefinitions[choiceUUID];
        const newKey = getNewChoiceUUID(choiceUUID);
        choicesMap[choiceUUID] = newKey;
        clonedStep.choiceDefinitions[newKey] = choice;
        delete clonedStep.choiceDefinitions[choiceUUID];
        clonedStep.choices[i] = newKey;
    });
    if (!shouldCloneLogic && clonedStep.conditions) {
        // when we clone an individual step, we don't want to clone the conditional logic
        // this resets the logic on the step to be a blank slate
        clonedStep.conditions.rules = [];
        clonedStep.conditions.ALWAYS = null;
    }
    else if (clonedStep.conditions && clonedStep.conditions.rules.length) {
        // the cloned step's conditions will have the original step's choice uuid's set against the rules
        // here we update those rules to have the cloned step's choice uuids
        clonedStep.conditions.rules.forEach((rule) => rule.requirements.forEach((requirement) => {
            // eslint-disable-next-line no-param-reassign
            requirement.selectedChoiceUUID =
                choicesMap[requirement.selectedChoiceUUID];
        }));
    }
    return clonedStep;
}
function assertUnreachable(_) {
    throw new Error("Didn't expect to get here");
}
function getNewChoiceUUID(uuid) {
    // 'Other' choice includes '-other' postfix, so when cloning
    // the choice, we need to make sure the new UUID also contains
    // this postfix
    return uuid.includes('-other') ? `${uuidv4()}-other` : uuidv4();
}
export function setMicrophoneRecordingPermission(mutable, microphone) {
    setRecordingPermission(mutable, 'microphone', microphone);
}
export function setWebcamRecordingPermission(mutable, webcam) {
    setRecordingPermission(mutable, 'webcam', webcam);
}
export function setScreenRecordingPermission(mutable, screen) {
    setRecordingPermission(mutable, 'screen', screen);
}
function setRecordingPermission(mutable, permission, enabled) {
    if (enabled) {
        unsetStepRecordingSettings(mutable);
    }
    mutable.recordingPermissions = Object.assign(Object.assign({}, getRecordingPermissions(mutable)), { [permission]: enabled });
    if (getAnyMediaEnabled(mutable)) {
        if (!mutable.mediaSettingsStep) {
            addMediaSettingsStep(mutable);
        }
    }
    else {
        removeMediaSettingsStep(mutable);
    }
}
function addMediaSettingsStep(mutable) {
    const step = createMediaSettingsStep({
        title: 'The researcher would like to record this session',
        isRequired: true,
        description: [
            {
                type: 'p',
                children: [
                    {
                        text: 'Please enable access below so the researcher can observe your session and understand your experience better.',
                    },
                ],
            },
        ],
    });
    mutable.mediaSettingsStep = step;
}
function removeMediaSettingsStep(mutable) {
    delete mutable.mediaSettingsStep;
}
// When whole test recording is enabled we disable recording on any prototype or
// website task so we don't generate multiple recordings
function unsetStepRecordingSettings(mutable) {
    getAllSteps(mutable).forEach((step) => {
        if ('recordingPermissions' in step) {
            StepUtils.setMicrophoneRecordingPermission(step, false);
            StepUtils.setWebcamRecordingPermission(step, false);
            StepUtils.setScreenRecordingPermission(step, false);
        }
    });
}
